import React, { FC, ReactNode, useContext } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import { Banner } from "@pepdirect/v3/ui/Banner";
import { TenantContext } from "v3/context/tenant";
import { BannerContext } from "v3/context/banner";
import { LoadingPage } from "v3/components/LoadingPage";
import { PortalHeader } from "v3/components/portal/Header";
import { PortalFooter } from "v3/components/portal/Footer";
import { Crumb, PortalBreadcrumbs } from "v3/components/portal/Breadcrumbs";
import { useCurrentUserIdQuery } from "v3/services/graphql/generated";
import { PageRoutes } from "v3/constants/pageRoutes";
import {
  StyledBanner,
  StyledContent,
  StyledFooter,
  StyledPage,
  StyledPageContainer,
} from "./index.styled";

interface PortalPrivatePageWrapperProps {
  children: ReactNode;
  crumbs?: Crumb[];
  title?: string;
}

/**
 * Used on pages in portal where a user is logged in
 */
export const PortalPrivatePageWrapper: FC<PortalPrivatePageWrapperProps> = ({
  children,
  crumbs,
  title,
}) => {
  const { tenant } = useContext(TenantContext);
  const {
    bannerDetails,
    setBannerDetails,
    bannerAutoDismiss,
    setBannerAutoDismiss,
  } = useContext(BannerContext);

  const { loading: currentUserIdLoading, data: currentUserId } =
    useCurrentUserIdQuery();
  const router = useRouter();

  if (currentUserIdLoading) return <LoadingPage />;

  if (!currentUserId?.currentUser) {
    router.push({
      pathname: PageRoutes.signIn,
      query: { successRedirectTo: router.asPath },
    });
    return null;
  }

  return (
    <>
      <Head>
        <title>
          {title || `${tenant?.brandConfig.title} Dashboard`.trim()}
        </title>
      </Head>

      <StyledPageContainer>
        <PortalHeader />
        {!!bannerDetails && (
          <StyledBanner>
            <Banner
              {...bannerDetails}
              setBannerDetails={setBannerDetails}
              bannerAutoDismiss={bannerAutoDismiss}
              setBannerAutoDismiss={setBannerAutoDismiss}
            />
          </StyledBanner>
        )}
        {crumbs && <PortalBreadcrumbs crumbs={crumbs} />}
        <StyledPage>
          <StyledContent>{children}</StyledContent>
        </StyledPage>
      </StyledPageContainer>

      <StyledFooter>
        <PortalFooter />
      </StyledFooter>
    </>
  );
};
